import React, {useState} from "react";
import { useSelector} from "react-redux";
import LoadingComponent from "../../components/LoadingComponent";
import {useTranslation} from "react-i18next";
import {Employee, EmploymentStatus} from "../../app/interfaces";
import {useNavigate} from "react-router-dom";
import useFetchEmployees from "../../hooks/useFetchEmployees";
import {
    pendingRegistrationStatuses,
} from "../../utils/constants";
import defaultAvatarImage from '../../assets/images/avatar.svg';
import CountryFlagIcon from "../../components/CountryFlagIcon";
import {countryNameByCode} from "../../utils/countryCodes";
import {fullNameForUser, isValidString} from "../../utils";
import {Table, TableBody, TableHeader, TableNavigation} from "../../components/Table";
import {
    TableDataCell,
    TableRow,
    TableHeaderCell
} from "../../components/Table/styles";
import DataTableLayout, {EmptyDataTableLayout} from "../../layouts/DataTableLayout/DataTableLayout";
import {
    DataTableLayoutFilter,
    DataTableLayoutFooter,
    DataTableLayoutHeader,
    DataTableLayoutTitle,
} from "../../layouts/DataTableLayout/styles";
import {EmployeeAddress, EmployeeIdentification, EmployeeCountry, TaxInfo} from "./styles";
import {Filter, FlexColumn, SearchBox, StatusPill} from "../../components";
import {allEmployeesFetchError, allEmployeesFetchState, selectedEmployeesPaginated} from "../../app/employee/selectors";

interface PendingRegistrationsProps {
    itemsPerPage: number;
}

const PendingRegistrations = ({itemsPerPage}: PendingRegistrationsProps) => {

    useFetchEmployees()

    const [currentPage, setCurrentPage] = useState<number>(1)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [employmentStatuses, setEmploymentStatuses] = useState<EmploymentStatus[]>([])

    const employees = useSelector(
        selectedEmployeesPaginated(
            currentPage,
            itemsPerPage,
            (employmentStatuses.length ? employmentStatuses : pendingRegistrationStatuses),
            searchTerm
        )
    );

    const fetchState = useSelector(allEmployeesFetchState)
    const error = useSelector(allEmployeesFetchError)

    const navigate = useNavigate();

    const {t} = useTranslation();

    const dataTableTitle = t('pending_registrations')

    const handleOpenEmployee = (employee: Employee) => {
        navigate(`/employee/${employee._id}`)
    }

    if (fetchState === 'loading' || fetchState === 'idle') {
        return <EmptyDataTableLayout title={dataTableTitle}>
            <LoadingComponent/>
        </EmptyDataTableLayout>
    }

    if (error) {
        console.warn(error)
        return <EmptyDataTableLayout title={dataTableTitle}>
            {error}
        </EmptyDataTableLayout>
    }

    if (!employees.items.length) {
        return <EmptyDataTableLayout
            title={dataTableTitle}
            filters={<DataTableFilters setSearchTerm={setSearchTerm} filters={employmentStatuses} setFilters={setEmploymentStatuses}/>}
        >
            {t('no_employees_found') as string}
        </EmptyDataTableLayout>
    }

    return <DataTableLayout>
        <DataTableLayoutHeader>
            <DataTableLayoutTitle>
                {dataTableTitle}
            </DataTableLayoutTitle>
            <DataTableFilters setSearchTerm={setSearchTerm} filters={employmentStatuses} setFilters={setEmploymentStatuses}/>
        </DataTableLayoutHeader>
        <Table>
            <TableHeader>
                <TableHeaderCell>{t('employee')}</TableHeaderCell>
                <TableHeaderCell>{t('location')}</TableHeaderCell>
                <TableHeaderCell className="md-hide">{t('tax_id_vat')}</TableHeaderCell>
                <TableHeaderCell style={{textAlign: 'center'}}>{t('status')}</TableHeaderCell>
            </TableHeader>
            <TableBody>
                {employees.items.map((e, i) => EmployeeRow(e, i, handleOpenEmployee, t))}
            </TableBody>
        </Table>
        <DataTableLayoutFooter>
            <TableNavigation
                pageLimit={itemsPerPage}
                summary={employees.summary}
                setPage={setCurrentPage}
            />
        </DataTableLayoutFooter>
    </DataTableLayout>
}

interface DataTableFiltersProps {
    setSearchTerm: Function
    filters: string[]
    setFilters: Function
}
const DataTableFilters = ({setSearchTerm, filters, setFilters }: DataTableFiltersProps) => {
    return <DataTableLayoutFilter>
        <SearchBox
            placeholder="Search employee"
            setSearchTerm={setSearchTerm}
        />
        <Filter
            filters={pendingRegistrationStatuses}
            selectedFilters={filters}
            setSelectedFilters={setFilters}
        />
    </DataTableLayoutFilter>
}

const EmployeeRow = (employee: Employee, index: number, handleOpenEmployee: Function, translate: Function) => {
    return <TableRow key={index} clickable onClick={() => handleOpenEmployee(employee)}>
        <TableDataCell>
            <EmployeeIdentification>
                {/*@ts-ignore*/}
                <img alt="avatar" src={isValidString(employee?.avatar) ? `${process.env.REACT_APP_API_URL}/${employee.avatar}` : defaultAvatarImage}/>
                <FlexColumn>
                    <h3>{fullNameForUser(employee)}</h3>
                    <h4>{employee.activity}</h4>
                </FlexColumn>
            </EmployeeIdentification>
        </TableDataCell>
        <TableDataCell>
            <EmployeeAddress>
                <h3>{employee.address}</h3>
                <EmployeeCountry countryName={countryNameByCode(employee.tax_residence) as string}
                                 countryCode={employee.tax_residence}>
                    <CountryFlagIcon countryCode={employee.tax_residence}/>
                    {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                    <h6></h6>
                </EmployeeCountry>
            </EmployeeAddress>
        </TableDataCell>
        <TableDataCell className='md-hide'>
            <TaxInfo>
                <h3>{translate('tax_id', {tax_id: employee.tax_id})}</h3>
            </TaxInfo>
        </TableDataCell>
        <TableDataCell style={{textAlign: "center"}}>
            <FlexColumn align="center" justify="center" style={{height: '100%'}}>
                <StatusPill
                    status={employee.status}
                >
                    {translate(employee.status)}
                </StatusPill>
            </FlexColumn>
        </TableDataCell>
    </TableRow>
}

export default PendingRegistrations;
